/* ============================================================
   NLO THEME — theme.js
   Navigation, scroll behavior, IntersectionObserver reveals,
   mobile drawer, cart opener triggers
   ============================================================ */

'use strict';

// ── Scroll Reveal ──────────────────────────────────────────
const revealObserver = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      entry.target.classList.add('is-visible');
      revealObserver.unobserve(entry.target);
    }
  });
}, { threshold: 0.1, rootMargin: '0px 0px -60px 0px' });

document.querySelectorAll('.reveal, .section-divider').forEach(el => {
  revealObserver.observe(el);
});

// ── Sticky Header ──────────────────────────────────────────
const header = document.querySelector('.site-header');
const SCROLL_THRESHOLD = 80;

function handleHeaderScroll() {
  if (window.scrollY > SCROLL_THRESHOLD) {
    header?.classList.add('is-scrolled');
  } else {
    header?.classList.remove('is-scrolled');
  }
}

if (header) {
  window.addEventListener('scroll', handleHeaderScroll, { passive: true });
  handleHeaderScroll();
}

// ── Mobile Nav Drawer ──────────────────────────────────────
const mobileNavDrawer = document.getElementById('mobile-nav-drawer');
const mobileNavOverlay = document.getElementById('mobile-nav-overlay');
const mobileNavClose = document.getElementById('mobile-nav-close');
const hamburgerBtn = document.querySelector('.js-open-nav');

function openMobileNav() {
  mobileNavDrawer?.classList.add('is-open');
  mobileNavDrawer?.setAttribute('aria-hidden', 'false');
  document.body.classList.add('no-scroll');
}

function closeMobileNav() {
  mobileNavDrawer?.classList.remove('is-open');
  mobileNavDrawer?.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('no-scroll');
}

hamburgerBtn?.addEventListener('click', openMobileNav);
mobileNavClose?.addEventListener('click', closeMobileNav);
mobileNavOverlay?.addEventListener('click', closeMobileNav);

// Mobile nav sub-menu toggles
document.querySelectorAll('.mobile-nav-item__toggle').forEach(toggle => {
  toggle.addEventListener('click', () => {
    const item = toggle.closest('.mobile-nav-item');
    const isOpen = item.classList.contains('is-expanded');
    // Close all others
    document.querySelectorAll('.mobile-nav-item.is-expanded').forEach(el => {
      el.classList.remove('is-expanded');
      el.querySelector('.mobile-nav-item__toggle')?.setAttribute('aria-expanded', 'false');
    });
    if (!isOpen) {
      item.classList.add('is-expanded');
      toggle.setAttribute('aria-expanded', 'true');
    }
  });
});

// ── Announcement Bar Dismiss ───────────────────────────────
const announcementBar = document.querySelector('.announcement-bar');
const announcementClose = document.querySelector('.announcement-bar__close');

announcementClose?.addEventListener('click', () => {
  announcementBar?.classList.add('is-hidden');
  try { sessionStorage.setItem('nlo-announcement-dismissed', '1'); } catch (e) {}
});

try {
  if (sessionStorage.getItem('nlo-announcement-dismissed') === '1') {
    announcementBar?.classList.add('is-hidden');
  }
} catch (e) {}

// ── Cart Drawer Trigger (open from header icon) ────────────
const cartTriggerBtns = document.querySelectorAll('.js-open-cart');
cartTriggerBtns.forEach(btn => {
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    window.NLOCart?.open();
  });
});

// ── Keyboard trap / Escape close ──────────────────────────
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    closeMobileNav();
    window.NLOCart?.close();
    document.querySelectorAll('.nlo-modal.is-open').forEach(modal => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
    });
  }
});

// ── Accordion (generic) ────────────────────────────────────
document.querySelectorAll('.accordion__trigger').forEach(trigger => {
  trigger.addEventListener('click', () => {
    const item = trigger.closest('.accordion__item');
    const isOpen = item.classList.contains('is-open');
    item.closest('.accordion')?.querySelectorAll('.accordion__item.is-open').forEach(openItem => {
      openItem.classList.remove('is-open');
      openItem.querySelector('.accordion__trigger')?.setAttribute('aria-expanded', 'false');
    });
    if (!isOpen) {
      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
    }
  });
});

// ── Collapsible Sections ───────────────────────────────────
document.querySelectorAll('.collapsible-section__header').forEach(header => {
  header.addEventListener('click', () => {
    const section = header.closest('.collapsible-section');
    section?.classList.toggle('is-open');
  });
});

// ── Image With Text Background Assignment ─────────────────
// Assign bg-image from data attribute
document.querySelectorAll('[data-bg-image]').forEach(el => {
  const src = el.dataset.bgImage;
  if (src) el.style.backgroundImage = `url('${src}')`;
});

// ── Slider: Featured ──────────────────────────────────────
class NLOSlider {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('.slider__track');
    this.slides = el.querySelectorAll('.slider__slide');
    this.dots = el.querySelectorAll('.slider__dot');
    this.prevBtn = el.querySelector('.slider__prev');
    this.nextBtn = el.querySelector('.slider__next');
    this.current = 0;
    this.total = this.slides.length;
    this.autoplayInterval = null;
    this.autoplayDelay = parseInt(el.dataset.autoplay, 10) || 5000;
    if (this.total < 2) return;
    this.init();
  }
  init() {
    this.prevBtn?.addEventListener('click', () => { this.go(this.current - 1); this.resetAutoplay(); });
    this.nextBtn?.addEventListener('click', () => { this.go(this.current + 1); this.resetAutoplay(); });
    this.dots.forEach((dot, i) => { dot.addEventListener('click', () => { this.go(i); this.resetAutoplay(); }); });
    this.startAutoplay();
    this.update();
    // Touch/swipe
    let startX = 0;
    this.track?.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    this.track?.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) { this.go(diff > 0 ? this.current + 1 : this.current - 1); this.resetAutoplay(); }
    });
  }
  go(index) {
    this.current = ((index % this.total) + this.total) % this.total;
    this.update();
  }
  update() {
    this.slides.forEach((s, i) => {
      s.classList.toggle('is-active', i === this.current);
      s.setAttribute('aria-hidden', i !== this.current ? 'true' : 'false');
    });
    this.dots.forEach((d, i) => { d.classList.toggle('is-active', i === this.current); });
  }
  startAutoplay() {
    this.autoplayInterval = setInterval(() => this.go(this.current + 1), this.autoplayDelay);
  }
  resetAutoplay() {
    clearInterval(this.autoplayInterval);
    this.startAutoplay();
  }
}

document.querySelectorAll('.nlo-slider').forEach(el => new NLOSlider(el));

// ── Smooth Scroll (anchor links) ──────────────────────────
document.querySelectorAll('a[href^="#"]').forEach(link => {
  link.addEventListener('click', e => {
    const target = document.getElementById(link.getAttribute('href').slice(1));
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
});

// ── Update Section Padding From Schema Settings ────────────
document.querySelectorAll('[data-pt][data-pb]').forEach(el => {
  el.style.setProperty('--section-pt', el.dataset.pt + 'px');
  el.style.setProperty('--section-pb', el.dataset.pb + 'px');
});

// ── Export for use by other modules ───────────────────────
window.NLOTheme = { revealObserver };
